import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { supabase } from '@/integrations/supabase/client';
import { AuthContextType, AuthUser } from '@/types/auth';
import { TEST_USERS, AUTH_TIMEOUTS, STORAGE_KEYS } from '@/constants';
import { createDefaultUser, createAuthUserFromEmployee, ensureEmployeeRecord } from '@/utils/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: supabaseUser, loading: supabaseLoading, session, refreshCounter } = useSupabaseAuth();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const initializeAuth = async () => {
      console.log('Initializing auth context...', { 
        supabaseUser: supabaseUser?.email, 
        supabaseLoading, 
        session: !!session,
        refreshCounter 
      });
      
      try {
        if (supabaseUser) {
          console.log('Processing Supabase user:', supabaseUser.email);
          
          // Set timeout for fallback
          timeoutId = setTimeout(() => {
            console.log('Employee lookup timeout, using basic user data');
            setUser(createDefaultUser(supabaseUser));
            setLoading(false);
          }, AUTH_TIMEOUTS.EMPLOYEE_LOOKUP);

          try {
            const employee = await ensureEmployeeRecord(supabaseUser);
            
            if (timeoutId) clearTimeout(timeoutId);

            if (employee) {
              console.log('Employee data processed:', employee.name);
              try {
                const authUser = await createAuthUserFromEmployee(employee);
                console.log('User permissions updated:', authUser.permissions);
                setUser(authUser);
                setLastRefreshTime(new Date());
              } catch (permissionError) {
                console.error('Error getting user permissions:', permissionError);
                // Fallback to default user without permissions
                setUser(createDefaultUser(supabaseUser));
              }
            } else {
              console.log('Using default user data');
              setUser(createDefaultUser(supabaseUser));
            }
          } catch (employeeError) {
            console.error('Error processing employee data:', employeeError);
            if (timeoutId) clearTimeout(timeoutId);
            setUser(createDefaultUser(supabaseUser));
          }
        } else {
          // Load saved test user
          const savedUser = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
          if (savedUser) {
            console.log('Loading saved test user');
            setUser(JSON.parse(savedUser));
          } else {
            console.log('No user found');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (timeoutId) clearTimeout(timeoutId);
        
        if (supabaseUser) {
          setUser(createDefaultUser(supabaseUser));
        }
      } finally {
        setLoading(false);
      }
    };

    if (!supabaseLoading) {
      initializeAuth();
    } else {
      timeoutId = setTimeout(() => {
        console.log('General loading timeout, forcing loading to false');
        setLoading(false);
      }, AUTH_TIMEOUTS.GENERAL_LOADING);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [supabaseUser, supabaseLoading, refreshCounter]);

  const login = async (credentials: { username: string; password: string }): Promise<{ success: boolean; error?: string }> => {
    // تم إلغاء تسجيل الدخول التجريبي - النظام يدعم Supabase فقط
    return { success: false, error: 'يرجى استخدام نظام المصادقة الحقيقي' };
  };

  const logout = async (): Promise<void> => {
    if (supabaseUser) {
      await supabase.auth.signOut();
    }
    
    setUser(null);
    localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
  };

  // Add automatic permission refresh every 10 minutes
  useEffect(() => {
    if (!user || !supabaseUser) return;

    const permissionRefreshInterval = setInterval(async () => {
      console.log('Auto-refreshing user permissions...');
      try {
        const employee = await ensureEmployeeRecord(supabaseUser);
        if (employee) {
          const authUser = await createAuthUserFromEmployee(employee);
          console.log('Permissions auto-refreshed:', authUser.permissions);
          setUser(authUser);
          setLastRefreshTime(new Date());
        }
      } catch (error) {
        console.error('Failed to auto-refresh permissions:', error);
      }
    }, 10 * 60 * 1000); // Every 10 minutes

    return () => clearInterval(permissionRefreshInterval);
  }, [user, supabaseUser]);

  return (
    <AuthContext.Provider value={{
      user,
      supabaseUser,
      loading,
      login,
      logout,
      lastRefreshTime
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
